import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Zoom,
  Tooltip,
  Avatar,
} from "@mui/material";
import "../styles/LanguageSwitcherCircular.scss";
import i18next from "i18next";
import { useNavigate, useLocation } from "react-router-dom";

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji: string;
}

export const LanguageSwitcherCircular: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();

  const languages: Language[] = [
    {
      code: "en",
      name: "English",
      nativeName: "English",
      flagEmoji: "🇬🇧",
    },
    {
      code: "ar",
      name: "Arabic",
      nativeName: "العربية",
      flagEmoji: "🇸🇦",
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    // Change the i18n language
    i18n.changeLanguage(lng);

    // Update document direction
    document.dir = lng === "ar" ? "rtl" : "ltr";

    // Navigate to the corresponding language URL
    navigate(`/${lng}${location.search}`);

    handleClose();
  };

  // Get current language
  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  return (
    <Box className="language-switcher-circular">
      <Tooltip
        title={t("change_language", "Change Language")}
        arrow
        TransitionComponent={Zoom}
        placement="left"
      >
        <IconButton
          onClick={handleClick}
          className="flag-button"
          aria-controls={open ? "language-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          color="primary"
        >
          <Box className="flag-container">
            <span className="flag-emoji">{currentLanguage.flagEmoji}</span>
          </Box>
        </IconButton>
      </Tooltip>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="language-menu"
        transformOrigin={{ horizontal: "right", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{
          elevation: 3,
          className: "language-menu-paper",
        }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            selected={i18n.language === language.code}
            className="language-menu-item"
          >
            <span className="menu-flag-emoji">{language.flagEmoji}</span>
            <Typography
              fontFamily={i18next.language === "ar" ? "Somar" : "DM Sans"}
              variant="body1"
              className="language-name"
            >
              {language.nativeName}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcherCircular;
